import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Input, Checkbox, Button, Popover, InputNumber } from "antd";
import { SearchOutlined, FilterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import AdminDashLayout from "../../../Components/Admin/AdminDashLayout";
import {
  getApplicationsThunk,
  moveMultipleApplicantsToNextRoundThunk,
} from "../../../Store/Slices/Admin/jobApplication/jobApplicationThunks";
import ApplicationDetailsDrawer from "./ApplicationDetailsDrawer";
import ApplicantCard from "./ApplicantCard";
import ExportExcel from "./ExportExcel";
import ProtectedAction from "../../../Routes/ProtectedRoutes/ProtectedAction";
import { PERMISSIONS } from "../../../config/permission";

const columnStyle = {
  minWidth: 320,
  background: "#F8FAFC",
  padding: 16,
  borderRadius: 10,
  height: "80vh",
  overflowY: "auto",
  border: "1px solid #E5E7EB",
  display: "flex",
  flexDirection: "column",
};

const headerStyle = {
  fontWeight: 600,
  fontSize: 16,
  marginBottom: 10,
};

const JobApplicationsPage = () => {
  const { jobId } = useParams();
  const dispatch = useDispatch();

  const { applications } = useSelector((state) => state.admin.jobApplications);

  const [selectedApplication, setSelectedApplication] = useState(null);
  const [search, setSearch] = useState({});
  const [interactionFilters, setInteractionFilters] = useState({});
  const [selectedApplicants, setSelectedApplicants] = useState([]);

  const [isExportModelOpen, setIsExportModelOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    dispatch(getApplicationsThunk({ jobId }));
  }, [dispatch, jobId]);

  const rounds = useMemo(() => {
    const set = new Set();

    applications?.forEach((app) => {
      app.roundHistory?.forEach((r) => {
        set.add(r.roundName);
      });
    });

    return Array.from(set);
  }, [applications]);

  /* ---------------- FILTERING ---------------- */

  const getApplicantsByRound = (roundName) => {
    const filtered = applications.filter((a) => {
      if (!roundName) return !a.currentRound;
      return a.currentRound === roundName;
    });

    const searchText = search[roundName || "applied"]?.toLowerCase();
    const filter = interactionFilters[roundName || "applied"];

    let result = filtered;

    /* EMAIL SEARCH */

    if (searchText) {
      const emails = searchText
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean);

      result = result.filter((a) =>
        emails.some((email) =>
          a.applicantEmail?.toLowerCase().includes(email)
        )
      );
    }

    /* EMAIL / CALL FILTER */

    if (filter) {
      result = result.filter((app) => {
        const round = app.roundHistory?.find(
          (r) => r.roundName === app.currentRound
        );

        const mailCount = round?.mailCount || 0;
        const callCount = round?.callCount || 0;

        if (filter.mailCount !== undefined && mailCount < filter.mailCount)
          return false;

        if (filter.callCount !== undefined && callCount < filter.callCount)
          return false;

        return true;
      });
    }

    return result;
  };

  /* ---------------- SELECT APPLICANTS ---------------- */

  const toggleApplicantSelection = (id) => {
    setSelectedApplicants((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  /* ---------------- BULK MOVE ---------------- */

  const handleMoveSelected = () => {
    if (!selectedApplicants.length) return;

    dispatch(moveMultipleApplicantsToNextRoundThunk(selectedApplicants));
    setSelectedApplicants([]);
  };

  /* ---------------- FILTER UI ---------------- */

  const renderFilterContent = (key) => {
    const current = interactionFilters[key] || {};

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          Emails ≥{" "}
          <InputNumber
            min={0}
            size="small"
            value={current.mailCount}
            onChange={(val) =>
              setInteractionFilters((prev) => ({
                ...prev,
                [key]: { ...prev[key], mailCount: val },
              }))
            }
          />
        </div>

        <div>
          Calls ≥{" "}
          <InputNumber
            min={0}
            size="small"
            value={current.callCount}
            onChange={(val) =>
              setInteractionFilters((prev) => ({
                ...prev,
                [key]: { ...prev[key], callCount: val },
              }))
            }
          />
        </div>
      </div>
    );
  };

  /* ---------------- COLUMN RENDER ---------------- */

  const renderColumn = (roundName, title) => {
    const applicants = getApplicantsByRound(roundName);
    const isAppliedColumn = !roundName;
    const key = roundName || "applied";

    return (
      <div key={key} style={columnStyle}>
        <div style={headerStyle}>
          {title} ({applicants.length})

          {isAppliedColumn && selectedApplicants.length > 0 && (
            <Button
              type="primary"
              size="small"
              style={{ marginLeft: 10 }}
              onClick={handleMoveSelected}
            >
              Move Selected ({selectedApplicants.length})
            </Button>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <Input
            placeholder="Search candidate (emails comma separated)"
            prefix={<SearchOutlined />}
            size="large"
            style={{ flex: 1 }}
            value={search[key] || ""}
            onChange={(e) =>
              setSearch((prev) => ({
                ...prev,
                [key]: e.target.value,
              }))
            }
          />

          <Popover
            title="Interaction Filters"
            content={renderFilterContent(key)}
            trigger="click"
          >
            <Button icon={<FilterOutlined />} size="large" />
          </Popover>
        </div>

        <div style={{ flex: 1 }}>
          {applicants.map((app) => (
            <div
              key={app._id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
              }}
            >
              {isAppliedColumn && (
                <Checkbox
                  checked={selectedApplicants.includes(app._id)}
                  onChange={() => toggleApplicantSelection(app._id)}
                />
              )}

              <div style={{ flex: 1 }}>
                <ApplicantCard
                  applicant={app}
                  onClick={() => setSelectedApplication(app)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  /* ---------------- EXPORT EXCEL ---------------- */

  const handleExportExcel = (e) => {
    e.preventDefault();

    if (!applications?.length) {
      alert("No applications found");
      return;
    }

    const excelData = applications.map((app) => ({
      "Applicant Name": app.applicantName,
      Email: app.applicantEmail,
      Phone: app.applicantPhone,
      "Current Round": app.currentRound,
      Status: app.overallStatus,
      "Applied Date": new Date(app.createdAt).toLocaleDateString(),
      CTC: app.formData?.CTC || "",
      ECTC: app.formData?.ECTC || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Applications");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(data, "job-applications.xlsx");

    setIsExportModelOpen(false);
  };

  const handleCancel = () => {
    setIsExportModelOpen(false);
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <AdminDashLayout>
      <div className="p-6">
        <div className="flex justify-between items-start">
          <h1 className="mb-6 text-2xl font-bold">
            {applications[0]?.jobId?.title} Applications
          </h1>

          <ProtectedAction requiredPermission={PERMISSIONS.EXPORT_JOB}>
            <ExportExcel
              isExportModelOpen={isExportModelOpen}
              setIsExportModelOpen={setIsExportModelOpen}
              handleExportExcel={handleExportExcel}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              handleCancel={handleCancel}
            />
          </ProtectedAction>
        </div>

        <div style={{ display: "flex", gap: 16, overflowX: "auto" }}>
          {renderColumn(null, "Applied")}
          {rounds.map((round) => renderColumn(round, round))}
        </div>

        {selectedApplication && (
          <ApplicationDetailsDrawer
            application={selectedApplication}
            onClose={() => setSelectedApplication(null)}
          />
        )}
      </div>
    </AdminDashLayout>
  );
};

export default JobApplicationsPage;
